import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AssessmentQuestion } from '@/components/assessment/conversational/questionTypes';

export interface DbQuestion {
  id: string;
  question_key: string;
  question_text: string;
  question_type: string;
  options: any;
  help_text: string | null;
  placeholder_text: string | null;
  is_required: boolean;
  validation_rules: any;
  display_order: number;
  question_group?: string | null;
}

/**
 * Maps DB question_type to frontend AssessmentQuestion type
 */
function mapQuestionType(dbType: string): string {
  switch (dbType) {
    case 'single_select': return 'radio';
    case 'boolean': return 'radio';
    case 'multi_select': return 'checkbox';
    case 'scale': return 'slider';
    case 'percentage': return 'slider';
    case 'text': return 'text';
    case 'textarea': return 'text';
    case 'currency': return 'text';
    case 'date': return 'text';
    case 'year_picker': return 'year-picker';
    case 'month_selector': return 'month-selector';
    case 'number': return 'number-input';
    case 'repeating_group': return 'repeating-group';
    case 'ranking': return 'ranking';
    case 'allocation_input': return 'allocation_input';
    case 'welcome': return 'welcome';
    case 'completion': return 'completion';
    default: return 'radio';
  }
}

/**
 * Transforms DB options array into frontend options shape.
 */
function mapOptions(dbOptions: any[]): { value: string; label: string; score?: number; exclusive?: boolean }[] | undefined {
  if (!dbOptions || !Array.isArray(dbOptions)) return undefined;

  return dbOptions.map((opt: any) => ({
    value: opt.value,
    label: opt.label,
    score: opt.score,
    ...(opt.exclusive ? { exclusive: true } : {}),
  }));
}

/**
 * Transforms DB validation_rules.display_condition into frontend conditionalDisplay
 */
function mapConditionalDisplay(validationRules: any): any | undefined {
  const dc = validationRules?.display_condition;
  if (!dc) return undefined;

  // Compound OR/AND conditions
  if (dc.type === 'OR' || dc.type === 'AND') {
    return {
      type: dc.type,
      conditions: (dc.conditions || []).map((c: any) => ({
        dependsOn: c.depends_on,
        showWhen: Array.isArray(c.value) ? c.value : [c.value],
        operator: c.operator || 'equals',
      })),
    };
  }

  const { depends_on, operator, value } = dc;
  if (!depends_on) return undefined;

  if (operator === 'contains') {
    return {
      dependsOn: depends_on,
      showWhen: Array.isArray(value) ? value : [value],
      operator: 'contains',
    };
  }

  if (operator === 'not_equals') {
    return {
      dependsOn: depends_on,
      showWhen: [`__not:${value}`],
    };
  }

  if (operator === 'not_includes') {
    return {
      dependsOn: depends_on,
      showWhen: [`__not:${value}`],
    };
  }

  if (operator === 'in' && Array.isArray(value)) {
    return {
      dependsOn: depends_on,
      showWhen: value,
    };
  }

  if (operator === 'equals') {
    return {
      dependsOn: depends_on,
      showWhen: Array.isArray(value) ? value : [value],
    };
  }

  return undefined;
}

/**
 * Transforms a DB question row into the frontend AssessmentQuestion shape.
 * Also carries question_group through as a non-standard field for the composite renderer.
 */
export function transformDbQuestion(db: DbQuestion): AssessmentQuestion & { _questionGroup?: string | null; _dbId?: string; _excludeMatch?: any } {
  const frontendType = mapQuestionType(db.question_type);

  const base: any = {
    id: db.question_key || db.id,
    type: frontendType,
    title: db.question_text,
    tooltip: db.help_text || undefined,
    required: db.is_required,
    // Carry group metadata through for composite rendering
    _questionGroup: db.question_group ?? null,
    _dbId: db.id,
  };

  // Add options for radio/checkbox/select types
  if (['radio', 'checkbox', 'select'].includes(frontendType)) {
    // Handle boolean-type options stored as {trueLabel, falseLabel} object
    if (db.question_type === 'boolean' && db.options && !Array.isArray(db.options)) {
      base.options = [
        { value: 'yes', label: db.options.trueLabel || 'Yes' },
        { value: 'no', label: db.options.falseLabel || 'No' },
      ];
    } else {
      base.options = mapOptions(db.options);
    }
  }

  // Ranking options
  if (frontendType === 'ranking') {
    base.options = mapOptions(db.options);
  }

  // Allocation input: pass raw options (field definitions) through
  if (frontendType === 'allocation_input') {
    base.options = db.options;
  }

  // Add slider config for scale/percentage types
  if (frontendType === 'slider') {
    const vr = db.validation_rules;
    const scaleConfig = vr?.scale_config;
    if (scaleConfig) {
      // Legacy scale_config pattern
      base.minValue = scaleConfig.min || 1;
      base.maxValue = scaleConfig.max || 5;
      base.step = scaleConfig.step || 1;
      base.defaultValue = Math.floor((scaleConfig.min + scaleConfig.max) / 2);
      if (scaleConfig.labels && Array.isArray(scaleConfig.labels)) {
        base.labels = { min: scaleConfig.labels[0], max: scaleConfig.labels[scaleConfig.labels.length - 1] };
        if (scaleConfig.labels.length > 2) {
          base.stepLabels = scaleConfig.labels;
        }
      } else if (scaleConfig.min_label || scaleConfig.max_label) {
        base.labels = { min: scaleConfig.min_label || String(scaleConfig.min), max: scaleConfig.max_label || String(scaleConfig.max) };
      }
    } else if (vr?.min !== undefined || vr?.max !== undefined) {
      // Direct validation_rules pattern (A7 style: min/max/step/left_label/right_label/default_value)
      base.minValue = vr.min ?? 0;
      base.maxValue = vr.max ?? 100;
      base.step = vr.step ?? 1;
      base.defaultValue = vr.default_value ?? Math.floor(((vr.min ?? 0) + (vr.max ?? 100)) / 2);
      base.labels = {
        min: vr.left_label || String(vr.min ?? 0),
        max: vr.right_label || String(vr.max ?? 100),
      };
    } else {
      base.minValue = 0;
      base.maxValue = 100;
      base.step = 1;
      base.defaultValue = 50;
      base.labels = { min: '0', max: '100' };
    }
    // Star rating render hint
    if (db.validation_rules?.render_as === 'star_rating') {
      base.renderAs = 'star_rating';
    }
  }

  // max_selections for multi_select / checkbox questions
  if (frontendType === 'checkbox' && db.validation_rules?.max_selections) {
    base.maxSelections = db.validation_rules.max_selections;
  }

  // Mutual exclusion rule for composite group sub-questions
  if (db.validation_rules?.exclude_match) {
    base._excludeMatch = db.validation_rules.exclude_match;
  }

  // Year picker config
  if (frontendType === 'year-picker') {
    base.minYear = db.validation_rules?.min_year ?? 1950;
    base.maxYear = db.validation_rules?.max_year ?? new Date().getFullYear();
  }

  // Number input config
  if (frontendType === 'number-input') {
    base.minNum = db.validation_rules?.min;
    base.maxNum = db.validation_rules?.max;
    base.placeholder = db.placeholder_text || undefined;
  }

  // Repeating group config
  if (frontendType === 'repeating-group') {
    base.fields = db.validation_rules?.fields;
    base.addLabel = db.validation_rules?.add_label;
    base.minEntries = db.validation_rules?.min_entries ?? 1;
    base.maxEntries = db.validation_rules?.max_entries ?? 10;
    base.sumValidation = db.validation_rules?.sum_validation;
  }

  // Add conditional display logic
  const conditional = mapConditionalDisplay(db.validation_rules);
  if (conditional) {
    base.conditionalDisplay = conditional;
  }

  // Map default_value for auto-save when question is conditionally hidden
  if (db.validation_rules?.default_value !== undefined) {
    base.skipDefaultValue = String(db.validation_rules.default_value);
  }

  return base as AssessmentQuestion;
}

/**
 * Hook: Fetches assessment questions from DB and returns them in frontend shape.
 */
export function useAssessmentQuestions(assessmentId: string | null) {
  const [questions, setQuestions] = useState<AssessmentQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!assessmentId) {
      setQuestions([]);
      setLoading(false);
      return;
    }

    async function fetchQuestions() {
      setLoading(true);
      setError(null);

      try {
        const { data, error: dbError } = await supabase
          .from('assessment_questions')
          .select('*')
          .eq('assessment_id', assessmentId)
          .order('display_order', { ascending: true });

        if (dbError) throw dbError;
        if (!data || data.length === 0) {
          setQuestions([]);
          setLoading(false);
          return;
        }

        const transformed = data.map((row) => transformDbQuestion(row as unknown as DbQuestion));
        setQuestions(transformed);
      } catch (err: any) {
        console.error('Error fetching assessment questions:', err);
        setError(err.message || 'Failed to load questions');
      } finally {
        setLoading(false);
      }
    }

    fetchQuestions();
  }, [assessmentId]);

  return { questions, loading, error };
}
